/**
 * Validation + decomposition for an uploaded portfolio file.
 *
 * The shape mirrors the real portfolio export (local/chris-v1/docs/eitadata.json): top-level `phases`,
 * `phaseColors`, `serviceAreas`, and a `domains[]` array where each domain carries a source-assigned
 * stable `id`, a `name`, and `jtbds[]` (`_meta` is optional and not present in the real file). On
 * commit the file is decomposed into per-domain items (project ADR-0006); each domain's `domainId`
 * is `String(id)` — the file's stable identity, NOT a slug of the name — so a rename keeps identity.
 * Validation is structural and dependency-free; it accepts the export shape and rejects malformed input.
 */
import type {DiffDomain, DomainContent, SnapshotMeta} from './types';

/** Hard cap on an accepted upload (bytes). */
export const MAX_FILE_BYTES = 5 * 1024 * 1024;

export interface ValidationResult {
  valid: boolean;
  errors: string[];
}

interface JtbdLike {
  name?: unknown;
  phase?: unknown;
}

interface DomainLike {
  id?: unknown;
  name?: unknown;
  jtbds?: unknown;
}

/** True for an acceptable domain id in the file: a finite number or a non-empty string. */
function isValidId(v: unknown): v is number | string {
  return (typeof v === 'number' && Number.isFinite(v)) || (typeof v === 'string' && v.trim().length > 0);
}

interface PortfolioFileLike {
  _meta?: unknown;
  phases?: unknown;
  phaseColors?: unknown;
  serviceAreas?: unknown;
  domains?: unknown;
}

function isObject(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null && !Array.isArray(v);
}

/**
 * Validate parsed JSON against the portfolio-file schema. Pass the already-parsed object; JSON.parse
 * failures are the caller's concern (the upload handler rejects non-JSON before reaching here).
 */
export function validateSnapshotFile(input: unknown): ValidationResult {
  const errors: string[] = [];

  if (!isObject(input)) {
    return {valid: false, errors: ['file must be a JSON object']};
  }
  const file = input as PortfolioFileLike;

  // `_meta` is optional (absent in the real export); if present it must be an object.
  if (file._meta !== undefined && !isObject(file._meta)) errors.push('_meta: must be an object when present');

  const phases = file.phases;
  if (!Array.isArray(phases) || phases.length === 0 || !phases.every(p => typeof p === 'string')) {
    errors.push('phases: required non-empty array of strings');
  }

  if (!isObject(file.phaseColors)) errors.push('phaseColors: required object');

  if (!Array.isArray(file.serviceAreas) || file.serviceAreas.length === 0) {
    errors.push('serviceAreas: required non-empty array');
  } else {
    file.serviceAreas.forEach((area, i) => {
      if (!isObject(area) || typeof area.id !== 'string' || typeof area.name !== 'string') {
        errors.push(`serviceAreas[${i}]: requires string id and name`);
      }
    });
  }

  if (!Array.isArray(file.domains) || file.domains.length === 0) {
    errors.push('domains: required non-empty array');
  } else {
    const validPhases = Array.isArray(phases) ? new Set(phases as string[]) : null;
    const seenIds = new Set<string>();
    file.domains.forEach((entry, d) => {
      if (!isObject(entry)) {
        errors.push(`domains[${d}]: required object`);
        return;
      }
      const domain = entry as DomainLike;
      if (!isValidId(domain.id)) {
        errors.push(`domains[${d}].id: required (a number or non-empty string)`);
      } else {
        const id = String(domain.id);
        if (seenIds.has(id)) errors.push(`domains[${d}].id: "${id}" is not unique`);
        seenIds.add(id);
      }
      if (typeof domain.name !== 'string') {
        errors.push(`domains[${d}].name: required string`);
      }
      if (!Array.isArray(domain.jtbds)) {
        errors.push(`domains[${d}].jtbds: required array`);
      } else {
        domain.jtbds.forEach((jtbd, i) => {
          const j = jtbd as JtbdLike;
          if (!isObject(jtbd) || typeof j.name !== 'string') {
            errors.push(`domains[${d}].jtbds[${i}].name: required string`);
          }
          if (typeof j.phase !== 'string') {
            errors.push(`domains[${d}].jtbds[${i}].phase: required string`);
          } else if (validPhases && !validPhases.has(j.phase)) {
            errors.push(`domains[${d}].jtbds[${i}].phase: "${j.phase}" not in declared phases`);
          }
        });
      }
    });
  }

  return {valid: errors.length === 0, errors};
}

/** A domain extracted from a validated file, ready to reconcile (portfolio resolved server-side). */
export interface ExtractedDomain {
  domainId: string;
  name: string;
  content: DomainContent;
}

/**
 * Decompose a VALIDATED portfolio file into its shared chrome + per-domain records. Call only after
 * `validateSnapshotFile` returns valid.
 */
export function extractSnapshot(input: unknown): {meta: SnapshotMeta; domains: ExtractedDomain[]} {
  const file = input as {
    _meta: unknown;
    phases: string[];
    phaseColors: Record<string, unknown>;
    serviceAreas: unknown[];
    domains: DomainContent[];
  };
  const meta: SnapshotMeta = {
    meta: file._meta,
    phases: file.phases,
    phaseColors: file.phaseColors,
    serviceAreas: file.serviceAreas
  };
  const domains: ExtractedDomain[] = file.domains.map(content => ({
    domainId: String(content.id),
    name: content.name,
    content
  }));
  return {meta, domains};
}

// ─── Upload diff (spec 003) ───────────────────────────────────────────────────
//
// Pure, dependency-free comparison of an incoming (staged) domain set against the chris-v1 one, keyed
// by the stable `domainId`. Because identity is the file's id (not the name), a RENAME is a
// certainty — same id, changed name — and needs no heuristic. The API layer supplies the chris-v1
// domains (from DynamoDB) and annotates `removed` entries with owner-grant counts.

/** A chris-v1 domain as the diff needs it — its stable id, chris-v1 name, and portfolio. */
export interface CurrentDomain {
  domainId: string;
  name: string;
  portfolio: string;
}

/**
 * Partition incoming vs chris-v1 domains by the stable `domainId`:
 *   - **added**    — an incoming id not currently present.
 *   - **removed**  — a chris-v1 id absent from the upload (annotated with its `portfolio`).
 *   - **renamed**  — an id in both whose name changed (carries `previousName`; portfolio + owner
 *                    grants carry over automatically on commit since identity is unchanged).
 *   - **unchanged**— an id in both with the same name.
 */
export function diffDomains(
  incoming: ExtractedDomain[],
  current: CurrentDomain[]
): {added: DiffDomain[]; removed: DiffDomain[]; renamed: DiffDomain[]; unchanged: DiffDomain[]} {
  const currentById = new Map(current.map(d => [d.domainId, d]));
  const incomingIds = new Set(incoming.map(d => d.domainId));

  const added: DiffDomain[] = [];
  const renamed: DiffDomain[] = [];
  const unchanged: DiffDomain[] = [];
  for (const d of incoming) {
    const existing = currentById.get(d.domainId);
    if (!existing) added.push({domainId: d.domainId, name: d.name});
    else if (existing.name !== d.name)
      renamed.push({domainId: d.domainId, name: d.name, previousName: existing.name});
    else unchanged.push({domainId: d.domainId, name: d.name});
  }

  const removed: DiffDomain[] = current
    .filter(d => !incomingIds.has(d.domainId))
    .map(d => ({domainId: d.domainId, name: d.name, portfolio: d.portfolio}));

  return {added, removed, renamed, unchanged};
}
